/**
 * 分镜景别/运镜共享配置
 * 统一管理前后端展示文案、Prompt 关键词和后端补充描述
 */

export interface SceneShotTypeOption {
  value: string;
  label: string;
  description: string;
  promptKeyword: string;
  videoPromptLabel: string;
  optimizeLabel: string;
  storageAliases?: readonly string[];
  promptAliases?: readonly string[];
}

export interface SceneCameraMoveOption {
  value: string;
  label: string;
  description: string;
  promptKeyword: string;
  videoPromptLabel: string;
  optimizeLabel: string;
  storageAliases?: readonly string[];
  promptAliases?: readonly string[];
}

export const SCENE_SHOT_TYPES = [
  {
    value: 'none',
    label: '无',
    description: '使用提示词中的描述',
    promptKeyword: '',
    videoPromptLabel: '',
    optimizeLabel: '无',
  },
  {
    value: 'closeup',
    label: '特写',
    description: '聚焦细节，表现情感',
    promptKeyword: '特写',
    videoPromptLabel: '特写镜头',
    optimizeLabel: '特写',
    storageAliases: ['close_up'],
  },
  {
    value: 'close',
    label: '近景',
    description: '人物胸部以上',
    promptKeyword: '近景',
    videoPromptLabel: '近景',
    optimizeLabel: '近景',
    storageAliases: ['close_shot'],
  },
  {
    value: 'medium',
    label: '中景',
    description: '人物膝盖以上',
    promptKeyword: '中景',
    videoPromptLabel: '中景',
    optimizeLabel: '中景',
    storageAliases: ['medium_shot'],
  },
  {
    value: 'medium_close',
    label: '中近景',
    description: '人物腰部以上，兼顾表情与动作',
    promptKeyword: '中近景',
    videoPromptLabel: '中近景',
    optimizeLabel: '中近景',
  },
  {
    value: 'medium_long',
    label: '中远景',
    description: '兼顾人物动作与周围环境关系',
    promptKeyword: '中远景',
    videoPromptLabel: '中远景',
    optimizeLabel: '中远景',
  },
  {
    value: 'full',
    label: '全景',
    description: '人物全身及周围环境',
    promptKeyword: '全景',
    videoPromptLabel: '全景',
    optimizeLabel: '全景',
    storageAliases: ['full_shot'],
  },
  {
    value: 'wide',
    label: '远景',
    description: '展示环境全貌',
    promptKeyword: '远景',
    videoPromptLabel: '远景',
    optimizeLabel: '远景',
    storageAliases: ['long', 'long_shot'],
  },
  {
    value: 'extreme_wide',
    label: '大远景',
    description: '强调宏大环境与空间关系',
    promptKeyword: '大远景',
    videoPromptLabel: '大远景',
    optimizeLabel: '大远景',
  },
] as const satisfies readonly SceneShotTypeOption[];

export const SCENE_CAMERA_MOVES = [
  {
    value: 'none',
    label: '无',
    description: '使用提示词中的描述',
    promptKeyword: '',
    videoPromptLabel: '',
    optimizeLabel: '无',
  },
  {
    value: 'static',
    label: '静止',
    description: '镜头保持不动',
    promptKeyword: '静止',
    videoPromptLabel: '镜头固定',
    optimizeLabel: '固定',
    promptAliases: ['固定'],
  },
  {
    value: 'push',
    label: '推进',
    description: '镜头向前推进',
    promptKeyword: '推进',
    videoPromptLabel: '镜头缓慢推进',
    optimizeLabel: '推进',
    storageAliases: ['push_in'],
    promptAliases: ['推'],
  },
  {
    value: 'pull',
    label: '拉远',
    description: '镜头向后拉远',
    promptKeyword: '拉远',
    videoPromptLabel: '镜头缓慢拉远',
    optimizeLabel: '拉远',
    storageAliases: ['pull_out'],
    promptAliases: ['拉'],
  },
  {
    value: 'pan',
    label: '平移',
    description: '镜头水平移动',
    promptKeyword: '平移',
    videoPromptLabel: '镜头平移',
    optimizeLabel: '平移',
    promptAliases: ['摇'],
  },
  {
    value: 'tilt_up',
    label: '上摇',
    description: '镜头向上摇动，带出高度或视线延伸',
    promptKeyword: '上摇',
    videoPromptLabel: '镜头向上摇动',
    optimizeLabel: '上摇',
  },
  {
    value: 'tilt_down',
    label: '下摇',
    description: '镜头向下摇动，突出落点或压迫感',
    promptKeyword: '下摇',
    videoPromptLabel: '镜头向下摇动',
    optimizeLabel: '下摇',
  },
  {
    value: 'orbit',
    label: '环绕',
    description: '镜头环绕主体',
    promptKeyword: '环绕',
    videoPromptLabel: '镜头环绕',
    optimizeLabel: '环绕',
  },
  {
    value: 'follow',
    label: '跟随',
    description: '镜头跟随主体移动',
    promptKeyword: '跟随',
    videoPromptLabel: '镜头跟随',
    optimizeLabel: '跟随',
  },
  {
    value: 'zoom_in',
    label: '变焦推进',
    description: '通过变焦快速拉近主体，强化关注点',
    promptKeyword: '变焦推进',
    videoPromptLabel: '镜头变焦推进',
    optimizeLabel: '变焦推进',
    promptAliases: ['变焦', '推焦'],
  },
  {
    value: 'zoom_out',
    label: '变焦拉远',
    description: '通过变焦快速拉远视野，增强空间感',
    promptKeyword: '变焦拉远',
    videoPromptLabel: '镜头变焦拉远',
    optimizeLabel: '变焦拉远',
    promptAliases: ['拉焦'],
  },
] as const satisfies readonly SceneCameraMoveOption[];

export type SceneShotTypeValue = (typeof SCENE_SHOT_TYPES)[number]['value'];
export type SceneCameraMoveValue = (typeof SCENE_CAMERA_MOVES)[number]['value'];

export const SCENE_SHOT_TYPE_VALUES = SCENE_SHOT_TYPES.map((option) => option.value) as [
  SceneShotTypeValue,
  ...SceneShotTypeValue[],
];

export const SCENE_CAMERA_MOVE_VALUES = SCENE_CAMERA_MOVES.map((option) => option.value) as [
  SceneCameraMoveValue,
  ...SceneCameraMoveValue[],
];

export const SCENE_SHOT_TYPE_SELECT_OPTIONS = SCENE_SHOT_TYPES.map((option) => ({
  value: option.value,
  label: option.label,
}));

export const SCENE_CAMERA_MOVE_SELECT_OPTIONS = SCENE_CAMERA_MOVES.map((option) => ({
  value: option.value,
  label: option.label,
}));

export const SCENE_SHOT_TYPE_PROMPT_OPTIONS = SCENE_SHOT_TYPES.map((option) => ({
  label: option.label,
  keyword: option.promptKeyword,
}));

export const SCENE_CAMERA_MOVE_PROMPT_OPTIONS = SCENE_CAMERA_MOVES.map((option) => ({
  label: option.label,
  keyword: option.promptKeyword,
}));

function matchByStorageValue<T extends { value: string; storageAliases?: readonly string[] }>(
  options: readonly T[],
  value?: string | null
): T | undefined {
  if (!value) {
    return undefined;
  }

  return options.find((option) =>
    option.value === value || option.storageAliases?.includes(value)
  );
}

function matchByPromptKeyword<T extends { promptKeyword: string; promptAliases?: readonly string[] }>(
  options: readonly T[],
  keyword?: string | null
): T | undefined {
  if (keyword === undefined || keyword === null) {
    return undefined;
  }

  return options.find((option) =>
    option.promptKeyword === keyword || option.promptAliases?.includes(keyword)
  );
}

export function findSceneShotTypeByValue(value?: string | null) {
  return matchByStorageValue(SCENE_SHOT_TYPES, value);
}

export function findSceneCameraMoveByValue(value?: string | null) {
  return matchByStorageValue(SCENE_CAMERA_MOVES, value);
}

export function findSceneShotTypeByPromptKeyword(keyword?: string | null) {
  return matchByPromptKeyword(SCENE_SHOT_TYPES, keyword);
}

export function findSceneCameraMoveByPromptKeyword(keyword?: string | null) {
  return matchByPromptKeyword(SCENE_CAMERA_MOVES, keyword);
}

export function getSceneShotTypeLabel(value?: string | null, fallback = '-') {
  return findSceneShotTypeByValue(value)?.label ?? fallback;
}

export function getSceneCameraMoveLabel(value?: string | null, fallback = '-') {
  return findSceneCameraMoveByValue(value)?.label ?? fallback;
}

export function getSceneShotTypePromptLabel(keyword?: string | null, fallback = '无') {
  return findSceneShotTypeByPromptKeyword(keyword)?.label ?? fallback;
}

export function getSceneCameraMovePromptLabel(keyword?: string | null, fallback = '无') {
  return findSceneCameraMoveByPromptKeyword(keyword)?.label ?? fallback;
}

export function getSceneShotTypeVideoPromptLabel(value?: string | null, fallback = '') {
  return findSceneShotTypeByValue(value)?.videoPromptLabel ?? fallback;
}

export function getSceneCameraMoveVideoPromptLabel(value?: string | null, fallback = '') {
  return findSceneCameraMoveByValue(value)?.videoPromptLabel ?? fallback;
}

export function getSceneShotTypeOptimizeLabel(value?: string | null, fallback = '中景') {
  return findSceneShotTypeByValue(value)?.optimizeLabel ?? fallback;
}

export function getSceneCameraMoveOptimizeLabel(value?: string | null, fallback = '固定') {
  return findSceneCameraMoveByValue(value)?.optimizeLabel ?? fallback;
}
